import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { AppHeader } from "../components/site/AppHeader";
import { EmployeesPanel } from "../components/admin/EmployeesPanel";
import { RecordList } from "../components/admin/RecordList";
import { DetailDialog } from "../components/admin/DetailDialog";

const TABS = [["enquiries", "Enquiries & quotes"], ["applications", "Job applications"], ["employees", "Employees"]];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "enquiries";
  const setTab = (k) => setParams({ tab: k }, { replace: true });
  const [data, setData] = useState({ enquiries: [], applications: [] });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, a] = await Promise.all([api.get("/admin/enquiries"), api.get("/admin/applications")]);
      setData({ enquiries: Array.isArray(e.data) ? e.data : [], applications: Array.isArray(a.data) ? a.data : [] });
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error(formatApiError(err));
    }
  }, [logout]);

  useEffect(() => {
    if (user?.role === "admin") load();
    return undefined;
  }, [user, load]);

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = data[tab] ?? [];
    return term ? list.filter((it) => JSON.stringify(it).toLowerCase().includes(term)) : list;
  }, [data, tab, q]);

  if (user === null) return <div className="min-h-screen bg-[var(--ink)]" />;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/employee" replace />;

  const onStatus = async (it, status) => {
    try {
      await api.patch(`/admin/${tab}/${it.id}`, { status });
      setData((d) => ({ ...d, [tab]: d[tab].map((x) => (x.id === it.id ? { ...x, status } : x)) }));
      toast.success("Status updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Delete record for ${it.full_name}?`)) return;
    try {
      await api.delete(`/admin/${tab}/${it.id}`);
      setData((d) => ({ ...d, [tab]: d[tab].filter((x) => x.id !== it.id) }));
      setOpen(null);
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <main className="min-h-screen bg-[var(--ink)]" data-testid="admin-dashboard">
      <AppHeader onLogout={logout} home="/admin" />
      <div className="mx-auto max-w-[1280px] px-5 py-12 md:px-10">
        <h1 className="font-display text-5xl font-black uppercase md:text-7xl">Dashboard</h1>
        <div className="mt-8 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
          {[
            ["Enquiries", data.enquiries.length],
            ["New enquiries", data.enquiries.filter((x) => x.status === "new").length],
            ["Applications", data.applications.length],
            ["New applications", data.applications.filter((x) => x.status === "new").length],
          ].map(([l, n]) => (
            <div key={l} className="bg-[var(--ink)] p-5" data-testid={`stat-${l.toLowerCase().replace(/ /g, "-")}`}>
              <p className="overline text-[var(--bone-2)]">{l}</p>
              <p className="mt-2 font-display text-5xl font-black text-[var(--signal)]">{n}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[var(--signal)] text-[var(--ink)]" : "border border-white/20 text-[var(--bone)] hover:border-[var(--signal)]"}`}>
                {l}{data[k] ? ` (${data[k].length})` : ""}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 border-b border-white/25 py-2 md:w-72">
            <Search className="h-4 w-4 text-[var(--bone-2)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, service…" data-testid="admin-search-input"
              className="w-full bg-transparent text-sm text-[var(--bone)] placeholder:text-[#8f8a7e] focus:outline-none" />
          </label>
        </div>
        <div className="mt-6">
          {tab === "employees" ? <EmployeesPanel query={q} /> : <RecordList items={items} kind={tab} onOpen={setOpen} onStatus={onStatus} />}
        </div>
      </div>
      <DetailDialog item={open} onClose={() => setOpen(null)} onDelete={onDelete} />
    </main>
  );
}
